import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';

import { environment } from '../../../../environments/environment';
import { TokenService } from '../services/token.service';

const isRelativeUrl = (url: string): boolean =>
  !/^[a-z][a-z0-9+.-]*:\/\//i.test(url) && !url.startsWith('//');

const isRelativeApiUrl = (url: string): boolean => /^\/?api(?:\/|$|\?)/i.test(url);

const isAbsoluteApiPath = (pathname: string, apiPathname: string): boolean => {
  // When apiUrl has no specific path (just an origin, e.g. http://localhost:5000),
  // pathname is '/'. Any same-origin request is treated as API.
  if (apiPathname === '/') return true;
  const normalizedApiPath = apiPathname.endsWith('/') ? apiPathname.slice(0, -1) : apiPathname;
  return pathname === normalizedApiPath || pathname.startsWith(`${normalizedApiPath}/`);
};

const isApiUrl = (url: string): boolean => {
  if (isRelativeUrl(url)) return isRelativeApiUrl(url);
  try {
    const requestUrl = new URL(url);
    const apiUrl = new URL(environment.apiUrl);
    return (
      requestUrl.origin === apiUrl.origin && isAbsoluteApiPath(requestUrl.pathname, apiUrl.pathname)
    );
  } catch {
    return false;
  }
};

/** The refresh route authenticates via the refresh_token in the body and is
 *  policy-allowed only for anonymous requests — a bearer header would make
 *  the backend enforce the caller's role and reject the refresh with 401,
 *  logging the user out instead of renewing the session. */
const REFRESH_PATH = '/v2/authenticate/refresh';
/** The switch route is also anonymous-only (plus Zendera staff): with a
 *  terminal worker's or org admin's bearer the API answers 401. It
 *  authenticates via the refresh token and seq in the body. */
const SWITCH_PATH = '/v2/authenticate/switch';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const token = inject(TokenService).getAccessToken();
  if (!token) return next(req);
  if (req.headers.has('Authorization')) return next(req);
  if (!isApiUrl(req.url)) return next(req);
  if (req.url.endsWith(REFRESH_PATH) || req.url.endsWith(SWITCH_PATH)) return next(req);
  return next(req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }));
};
